import { Select, Modal } from "antd";
import React from "react";
import { FaCirclePlus, FaCircleMinus } from "react-icons/fa6";
import { toast } from "react-toastify";
import AsyncSelect from 'react-select/async';
import { api, API_BASE_URL } from "../../../config/api";
import { CANADIAN_PROVINCES, BRAZILIAN_STATES } from '../../../utils/states';
import { countries } from "../../shared/countrySelectInput/countries";
import { getAlpha2Code } from "../../../lib/helper/countryToAphlaCode";

const EstimatePopup = ({
    isOpen,
    onClose,
    onCalculate,
    formData,
    setFormData,
    eccnCodes,
    disabledCountries,
    popupFormData,
    setPopupFormData,
    popupValidationErrors,
    setPopupValidationErrors,
    isCalculating,
    handleAddItem,
    handleRemoveItem,
    handlePopupInputChange,
    loadHSCodeOptions,
    needsStateSelection,
    getAvailableStates,
    renderEccnOption
}) => {
    return (
        <Modal
            title={<h2 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-1">Calculate Estimate</h2>}
            open={isOpen}
            onCancel={onClose}
            width={1000}
            footer={[
                <button
                    key="calculate"
                    onClick={() => onCalculate()}
                    className="btnBg text-white p-2 rounded-md text-sm flex items-center justify-center gap-2"
                    disabled={isCalculating}
                >
                    {isCalculating ? "Calculating..." : "Calculate"}
                </button>,
            ]}
        >
            <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="flex flex-col gap-3 bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                        <label className="w-full text-sm font-medium text-gray-900 flex items-center gap-3">
                            <span className="flex items-center gap-2">
                                <span className="text-xl text-blue-500 rotate-[-45deg] inline-block">✈️</span>
                                Load Country
                            </span>
                            <span className="inline-block px-4 py-1 text-sm font-medium text-gray-900 bg-gray-200 rounded-md">
                                {formData.loadCountry || 'Not selected'}
                            </span>
                        </label>
                        {needsStateSelection(formData.loadCountry) && (
                            <div className="mt-3">
                                <label className="w-full text-sm font-medium text-gray-900" htmlFor="loadState">
                                    Load State <span className="text-red-500">*</span>
                                </label>
                                <div className={`mt-2 rounded-md ${popupValidationErrors['loadState'] ? "border-2 border-red-500" : "border border-gray-300"}`}>
                                    <Select
                                        showSearch
                                        id="loadState"
                                        options={getAvailableStates(formData.loadCountry)}
                                        onChange={(value) => setFormData(prev => ({ ...prev, loadState: value }))}
                                        value={formData.loadState || ""}
                                        style={{ width: "100%" }}
                                        placeholder="Select Load State"
                                        className="focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-3 bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                        <label className="w-full text-sm font-medium text-gray-900 flex items-center gap-3">
                            <span className="flex items-center gap-2">
                                <span className="text-xl text-blue-500 rotate-[135deg] inline-block">✈️</span>
                                Unload Country
                            </span>
                            <span className="inline-block px-4 py-1 text-sm font-medium text-gray-900 bg-gray-200 rounded-md">
                                {formData.unloadCountry || 'Not selected'}
                            </span>
                        </label>
                        {needsStateSelection(formData.unloadCountry) && (
                            <div className="mt-3">
                                <label className="w-full text-sm font-medium text-gray-900" htmlFor="unloadState">
                                    Unload State <span className="text-red-500">*</span>
                                </label>
                                <div className={`mt-2 rounded-md ${popupValidationErrors['unloadState'] ? "border-2 border-red-500" : "border border-gray-300"}`}>
                                    <Select
                                        showSearch
                                        id="unloadState"
                                        options={getAvailableStates(formData.unloadCountry)}
                                        onChange={(value) => setFormData(prev => ({ ...prev, unloadState: value }))}
                                        value={formData.unloadState || ""}
                                        style={{ width: "100%" }}
                                        placeholder="Select Unload State"
                                        className="focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="w-full flex gap-5 items-center mt-8">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold">Item details</h2>
                        <FaCirclePlus
                            className="text-[22px] text-[#03336e] cursor-pointer"
                            onClick={handleAddItem}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <h1 className="mt-2">Currency</h1>
                        <input
                            type="text"
                            className="p-1 w-full text-sm border rounded-md outline-none bg-gray-100"
                            name="itemsCurrency"
                            id="itemsCurrency"
                            value={formData.currency}
                            readOnly
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <h1 className="mt-2">Service</h1>
                        <input
                            type="text"
                            className="p-1 w-full text-sm border rounded-md outline-none bg-gray-100"
                            name="selectedService"
                            id="selectedService"
                            value={formData.selectedTemplate === "EOR & IOR" ? "EOR & IOR" : formData.selectedTemplate}
                            readOnly
                        />
                    </div>
                </div>

                <div>
                    {popupFormData.items.map((item, index) => (
                        <div key={`item-${index}`} className="md:px-0">
                            <h2 className="flex gap-3 items-center text-[16px] font-bold text-[gray] mt-6">
                                Item details
                                <button
                                    onClick={() => handleRemoveItem(index)}
                                    className="cursor-pointer"
                                >
                                    <FaCircleMinus />
                                </button>
                            </h2>
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                                <div className="flex flex-col gap-1 py-2">
                                    <label className="w-full text-sm font-normal" htmlFor={`itemName-${index}`}>
                                        <span className="text-red-500">*</span> Item Name
                                    </label>
                                    <input
                                        type="text"
                                        className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${popupValidationErrors[`item_${index}_itemName`] ? "border-red-500" : "active:border-cus-blue focus:border-cus-blue"}`}
                                        id={`itemName-${index}`}
                                        onChange={(e) => handlePopupInputChange(index, "itemName", e.target.value)}
                                        value={item.itemName}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 py-2">
                                    <label className="w-full text-sm font-normal" htmlFor={`quantity-${index}`}>
                                        <span className="text-red-500">*</span> Quantity
                                    </label>
                                    <input
                                        type="number"
                                        className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${popupValidationErrors[`item_${index}_quantity`] ? "border-red-500" : "active:border-cus-blue focus:border-cus-blue"}`}
                                        id={`quantity-${index}`}
                                        onChange={(e) => handlePopupInputChange(index, "quantity", e.target.value)}
                                        value={item.quantity}
                                        min="1"
                                        step="any"
                                    />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                                <div className="flex flex-col gap-1 py-2">
                                    <label className="w-full text-sm font-normal" htmlFor={`itemValue-${index}`}>
                                        <span className="text-red-500">*</span> Item Value
                                    </label>
                                    <input
                                        type="number"
                                        className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${popupValidationErrors[`item_${index}_itemValue`] ? "border-red-500" : "active:border-cus-blue focus:border-cus-blue"}`}
                                        id={`itemValue-${index}`}
                                        onChange={(e) => handlePopupInputChange(index, "itemValue", e.target.value)}
                                        value={item.itemValue}
                                        min="0.01"
                                        step="any"
                                    />
                                </div>
                                <div className="flex flex-col gap-1 py-2">
                                    <label className="w-full text-sm font-normal" htmlFor={`countryOrigin-${index}`}>
                                        <span className="text-red-500">*</span> Country of Origin
                                    </label>
                                    <div className={popupValidationErrors[`item_${index}_countryOrigin`] ? "border border-red-500" : ""}>
                                        <Select
                                            showSearch
                                            id={`countryOrigin-${index}`}
                                            className="w-full"
                                            options={countries.map((country, idx) => ({
                                                value: country,
                                                label: country,
                                                key: idx,
                                            }))}
                                            onChange={(value) => handlePopupInputChange(index, "countryOrigin", value)}
                                            value={item.countryOrigin}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 py-2 md:col-span-2">
                                    <label className="w-full text-sm font-normal">
                                        <span className="text-red-500">*</span> Item HS Code
                                    </label>

                                    <div className="flex items-center gap-2 mb-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`hsCodeSource-${index}`}
                                                value="database"
                                                checked={item.hsCodeSource !== 'manual'}
                                                onChange={(e) => {
                                                    const updatedItems = [...popupFormData.items];
                                                    updatedItems[index].hsCodeSource = 'database';
                                                    updatedItems[index].itemHSCode = ''; // Clear current value when switching
                                                    setPopupFormData(prev => ({ ...prev, items: updatedItems }));
                                                }}
                                                className="mr-1"
                                            />
                                            Search from Database
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`hsCodeSource-${index}`}
                                                value="manual"
                                                checked={item.hsCodeSource === 'manual'}
                                                onChange={(e) => {
                                                    const updatedItems = [...popupFormData.items];
                                                    updatedItems[index].hsCodeSource = 'manual';
                                                    updatedItems[index].itemHSCode = ''; // Clear current value when switching
                                                    setPopupFormData(prev => ({ ...prev, items: updatedItems }));
                                                }}
                                                className="mr-1"
                                            />
                                            Enter Manually
                                        </label>
                                    </div>

                                    {item.hsCodeSource === 'manual' ? (
                                        // Manual input field
                                        <input
                                            type="text"
                                            placeholder="Enter HS Code (first 6 digits must be numbers)"
                                            value={item.itemHSCode || ''}
                                            onChange={(e) => {
                                                let value = e.target.value;

                                                // Validation: first 6 characters must be digits
                                                if (value.length > 0) {
                                                    const first6 = value.substring(0, 6);
                                                    if (!/^\d{1,6}$/.test(first6)) {
                                                        // If first 6 characters are not all digits, prevent the change
                                                        return;
                                                    }
                                                }

                                                handlePopupInputChange(index, "itemHSCode", value);
                                            }}
                                            className={`w-full p-2 text-sm border rounded-md outline-none ${popupValidationErrors[`item_${index}_itemHSCode`] ? 'border-red-500' : 'border-gray-300 focus:border-blue-500'
                                                }`}
                                        />
                                    ) : (
                                        // Database search (existing AsyncSelect)
                                        <AsyncSelect
                                            key={`${index}-${item.itemHSCode || 'empty'}`}
                                            cacheOptions
                                            loadOptions={loadHSCodeOptions}
                                            defaultOptions
                                            onChange={(selectedOption) => {
                                                handlePopupInputChange(index, "itemHSCode", selectedOption ? selectedOption.value : '');
                                            }}
                                            value={item.itemHSCode ? { value: item.itemHSCode, label: item.itemHSCode } : null}
                                            placeholder="Search HS Code (min 6 digits)"
                                            isClearable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '32px',
                                                    height: '32px',
                                                    borderColor: popupValidationErrors[`item_${index}_itemHSCode`] ? 'red' : base.borderColor,
                                                    '&:hover': {
                                                        borderColor: popupValidationErrors[`item_${index}_itemHSCode`] ? 'red' : base.borderColor,
                                                    },
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    height: '32px',
                                                    padding: '0 8px',
                                                }),
                                                input: (base) => ({
                                                    ...base,
                                                    margin: '0',
                                                }),
                                                indicatorsContainer: (base) => ({
                                                    ...base,
                                                    height: '32px',
                                                }),
                                            }}
                                        />
                                    )}

                                    {popupValidationErrors[`item_${index}_itemHSCode`] && (
                                        <p className="text-red-500 text-xs mt-1">
                                            {typeof popupValidationErrors[`item_${index}_itemHSCode`] === 'string'
                                                ? popupValidationErrors[`item_${index}_itemHSCode`]
                                                : "HS Code is required"
                                            }
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {formData.selectedTemplate !== "EOR" && (
                    <div className="mt-4">
                        <div className={popupValidationErrors['requiredDoc'] ? "border border-red-500 p-2" : ""}>
                            <label className="w-full text-sm font-normal">
                                <span className="text-red-500">*</span> Import documentation required?
                            </label>
                            <div className="flex gap-4 items-center mt-2">
                                <label className="flex items-center text-sm">
                                    <input
                                        type="radio"
                                        name="requiredDoc"
                                        value="Required"
                                        checked={popupFormData.requiredDoc === "Required"}
                                        onChange={(e) => setPopupFormData({ ...popupFormData, requiredDoc: e.target.value })}
                                        className="mr-2 h-4 w-4 text-cus-blue focus:ring-cus-blue"
                                    />
                                    Required
                                </label>
                                <label className="flex items-center text-sm">
                                    <input
                                        type="radio"
                                        name="requiredDoc"
                                        value="not_Required"
                                        checked={popupFormData.requiredDoc === "not_Required"}
                                        onChange={(e) => setPopupFormData({ ...popupFormData, requiredDoc: e.target.value })}
                                        className="mr-2 h-4 w-4 text-cus-blue focus:ring-cus-blue"
                                    />
                                    Not Required
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-4">
                    <div className={popupValidationErrors['insuranceSelected'] ? "border border-red-500 p-2" : ""}>
                        <label className="w-full text-sm font-normal">
                            <span className="text-red-500">*</span> Do you need insurance?
                        </label>
                        <div className="flex gap-4 items-center mt-2">
                            <label className="flex items-center text-sm">
                                <input
                                    type="radio"
                                    name="insuranceSelected"
                                    value="YES"
                                    checked={popupFormData.insuranceSelected === "YES"}
                                    onChange={(e) => setPopupFormData({ ...popupFormData, insuranceSelected: e.target.value })}
                                    className="mr-2 h-4 w-4 text-cus-blue focus:ring-cus-blue"
                                />
                                Yes
                            </label>
                            <label className="flex items-center text-sm">
                                <input
                                    type="radio"
                                    name="insuranceSelected"
                                    value="NO"
                                    checked={popupFormData.insuranceSelected === "NO"}
                                    onChange={(e) => setPopupFormData({ ...popupFormData, insuranceSelected: e.target.value })}
                                    className="mr-2 h-4 w-4 text-cus-blue focus:ring-cus-blue"
                                />
                                No
                            </label>
                        </div>
                    </div>
                </div>

                {(formData.selectedTemplate === "EOR" || formData.selectedTemplate === "EOR & IOR") && (
                    <div className="mt-4">
                        <label className="w-full text-sm font-normal">
                            <span className="text-red-500">*</span> ECCN Code
                        </label>
                        <div className={popupValidationErrors['eccnCode'] ? "border border-red-500" : ""}>
                            <Select
                                showSearch
                                placeholder="Select ECCN Code"
                                value={popupFormData.eccnCode}
                                onChange={(value) => setPopupFormData({ ...popupFormData, eccnCode: value })}
                                style={{ width: "100%" }}
                                optionLabelProp="label"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.props.children.some(child =>
                                        child.toString().toLowerCase().includes(input.toLowerCase())
                                    )
                                }
                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                            >
                                {eccnCodes.map((eccn) => (
                                    <Select.Option
                                        key={eccn.eccn_code}
                                        value={eccn.eccn_code}
                                        label={eccn.eccn_code}
                                    >
                                        {renderEccnOption(eccn)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default EstimatePopup;      